import { createSelector, createSlice } from '@reduxjs/toolkit';
import { shallowEqual } from 'react-redux';
import { DEFAULT_PAGE_SIZE } from '../constants';
import {
  TransportReservation,
  TransportReservationRequest,
  TransportReservationResponse,
  TransportTripLeg,
} from '../models/transport-reservation.model';
import { AppDispatch, RootState } from '../store';
import { reservationAdapter } from '../entities/reservation.entity';
import reservationService from '@/services/reservationService';
import { TripLeg } from '../models/reservation.model';
import { mergeTripLegIntoTransportTripLeg } from '@/services/transportReservationMapper';
import { selectAllTripLegs } from './trip-leg.slice';
import {
  ReservationEventDetail,
  ReservationEventType,
} from '../events/reservation';

// Not used yet, but necessary for when page selectors need a prefill of
// the filters, pagination, sorting, etc.
const DEFAULT_PAGED_LIST: TransportReservationResponse = {
  currentPage: 1,
  totalPages: 0,
  itemCount: 0,
  filters: {
    passengerUUIDs: [],
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: 'reservationServiceDate',
    sortOrder: 'desc',
  },
  items: [],
};

// The reservation entity adapter contains all reservations, future and past,
// but we need to fetch future and past reservations separately, so we'll maintain
// separate loading and loaded flags for each. Selectors will be used to get future
// and past reservations from the entity adapter, based on reservationState (active, inactive)
type ReservationEntityState = {
  loadingAllReservations: boolean;
  loadedAllReservations: boolean;
  loadingFutureReservations: boolean;
  loadedFutureReservations: boolean;
  loadingPastReservations: boolean;
  loadedPastReservations: boolean;
  reservationEventEmitted: {
    detailType: ReservationEventType;
    detail: ReservationEventDetail;
  } | null;
};

const reservationSlice = createSlice({
  name: 'reservationV2',
  initialState: reservationAdapter.getInitialState<ReservationEntityState>({
    loadingAllReservations: false,
    loadedAllReservations: false,
    loadingFutureReservations: false,
    loadedFutureReservations: false,
    loadingPastReservations: false,
    loadedPastReservations: false,
    reservationEventEmitted: null,
  }),
  reducers: {
    setReservations: (
      state,
      action: { payload: TransportReservationResponse },
    ) => {
      reservationAdapter.setMany(state, action.payload.items);
    },
    setLoadingAllReservations: (state, action: { payload: boolean }) => {
      state.loadingAllReservations = action.payload;
    },
    setLoadedAllReservations: (state, action: { payload: boolean }) => {
      state.loadedAllReservations = action.payload;
    },
    setLoadingFutureReservations: (state, action: { payload: boolean }) => {
      state.loadingFutureReservations = action.payload;
    },
    setLoadedFutureReservations: (state, action: { payload: boolean }) => {
      state.loadedFutureReservations = action.payload;
    },
    setLoadingPastReservations: (state, action: { payload: boolean }) => {
      state.loadingPastReservations = action.payload;
    },
    setLoadedPastReservations: (state, action: { payload: boolean }) => {
      state.loadedPastReservations = action.payload;
    },
    setReservationEventEmitted: (
      state,
      action: {
        payload: {
          detailType: ReservationEventType;
          detail: ReservationEventDetail;
        } | null;
      },
    ) => {
      state.reservationEventEmitted = action.payload;
    },
    reservationActivated: (
      state,
      action: { payload: TransportReservation },
    ) => {
      reservationAdapter.addOne(state, action.payload);
    },
  },
});

const {
  setReservations,
  setLoadingAllReservations,
  setLoadedAllReservations,
  setLoadingFutureReservations,
  setLoadedFutureReservations,
  setLoadingPastReservations,
  setLoadedPastReservations,
  setReservationEventEmitted,
} = reservationSlice.actions;

/*
 * Fetch functions for use in components to fetch reservations from the API
 */
const fetchAllReservationsAsync = (request: TransportReservationRequest) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setLoadingAllReservations(true));
    dispatch(setLoadedAllReservations(false));
    reservationService.getPassengerTransportReservations(request).subscribe({
      next: (response) => {
        dispatch(setReservations(response));
        dispatch(setLoadingAllReservations(false));
        dispatch(setLoadedAllReservations(true));
      },
    });
  };
};

const fetchFutureReservationsAsync = (request: TransportReservationRequest) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setLoadingFutureReservations(true));
    dispatch(setLoadedFutureReservations(false));
    reservationService.getPassengerTransportReservations(request).subscribe({
      next: (response) => {
        dispatch(setReservations(response));
        dispatch(setLoadingFutureReservations(false));
        dispatch(setLoadedFutureReservations(true));
      },
    });
  };
};

const fetchPastReservationsAsync = (request: TransportReservationRequest) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setLoadingPastReservations(true));
    dispatch(setLoadedPastReservations(false));
    reservationService.getPassengerTransportReservations(request).subscribe({
      next: (response) => {
        dispatch(setReservations(response));
        dispatch(setLoadingPastReservations(false));
        dispatch(setLoadedPastReservations(true));
      },
    });
  };
};

/*
 * Selectors for use in components to get reservations from the state
 */
const reservationSelectors = reservationAdapter.getSelectors<RootState>(
  (state) => state.reservationV2,
);

// Utility function to check the tripLegV2 entities for any trip legs
// that were updated via realtime updates so we can merge them into the reservations
const mergeReservationsWithTripLegs = (
  reservations: TransportReservation[],
  tripLegEntities: TripLeg[],
) => {
  const tripLegsById = new Map<number, TripLeg>();
  const tripLegsByReferenceId = new Map<string, TripLeg>();
  for (const tripLeg of tripLegEntities) {
    if (tripLeg.tripLegID != null) {
      tripLegsById.set(tripLeg.tripLegID, tripLeg);
    }
    if (tripLeg.tripLegReferenceID) {
      tripLegsByReferenceId.set(tripLeg.tripLegReferenceID, tripLeg);
    }
  }

  const mergeTransportTripLeg = (
    transportTripLeg: TransportTripLeg,
  ): TransportTripLeg => {
    const updated =
      tripLegsById.get(transportTripLeg.tripLegID) ??
      tripLegsByReferenceId.get(transportTripLeg.tripLegReferenceID);
    return updated
      ? mergeTripLegIntoTransportTripLeg(transportTripLeg, updated)
      : transportTripLeg;
  };

  return reservations.map((reservation) => ({
    ...reservation,
    tripLegs: reservation.tripLegs.map(mergeTransportTripLeg),
  }));
};

const mergedTripLegsSelectorOptions = {
  memoizeOptions: { resultEqualityCheck: shallowEqual },
};

// Select all reservations
const selectAllReservations = createSelector(
  [reservationSelectors.selectAll, selectAllTripLegs],
  mergeReservationsWithTripLegs,
  mergedTripLegsSelectorOptions,
);
const selectLoadingAllReservations = (state: RootState) =>
  state.reservationV2.loadingAllReservations;
const selectLoadedAllReservations = (state: RootState) =>
  state.reservationV2.loadedAllReservations;

// Select future reservations
const selectActiveReservations = createSelector(
  [reservationSelectors.selectAll],
  (reservations) =>
    reservations.filter(
      (reservation) => reservation.reservationState === 'active',
    ),
);

const selectFutureReservations = createSelector(
  [selectActiveReservations, selectAllTripLegs],
  mergeReservationsWithTripLegs,
  mergedTripLegsSelectorOptions,
);
const selectLoadingFutureReservations = (state: RootState) =>
  state.reservationV2.loadingFutureReservations;
const selectLoadedFutureReservations = (state: RootState) =>
  state.reservationV2.loadedFutureReservations;

// Select past reservations
const selectInactiveReservations = createSelector(
  [reservationSelectors.selectAll],
  (reservations) =>
    reservations.filter(
      (reservation) => reservation.reservationState === 'inactive',
    ),
);

const selectPastReservations = createSelector(
  [selectInactiveReservations, selectAllTripLegs],
  mergeReservationsWithTripLegs,
  mergedTripLegsSelectorOptions,
);
const selectLoadingPastReservations = (state: RootState) =>
  state.reservationV2.loadingPastReservations;
const selectLoadedPastReservations = (state: RootState) =>
  state.reservationV2.loadedPastReservations;

const selectReservationEventEmitted = (state: RootState) =>
  state.reservationV2.reservationEventEmitted;

export {
  reservationSlice,
  reservationSelectors,
  fetchAllReservationsAsync,
  fetchFutureReservationsAsync,
  fetchPastReservationsAsync,
  selectAllReservations,
  selectLoadingAllReservations,
  selectLoadedAllReservations,
  selectFutureReservations,
  selectLoadingFutureReservations,
  selectLoadedFutureReservations,
  selectPastReservations,
  selectLoadingPastReservations,
  selectLoadedPastReservations,
  selectReservationEventEmitted,
};
